import { z } from 'zod';

import { type FlowRunBlockWithData, FlowRunState, type FlowRunWithData } from './flowRun.js';
import { oneOrMany } from '../../utils.js';

const _flowRunExploreFindAllRequestQuerySchema = z.object({
  flowId: z.uuid(),
  limit: z.coerce.number().optional(),
  offset: z.coerce.number().optional(),
  createdBefore: z.string().datetime().optional(),
  createdAfter: z.string().datetime().optional(),
  startedBefore: z.string().datetime().optional(),
  startedAfter: z.string().datetime().optional(),
  endedBefore: z.string().datetime().optional(),
  endedAfter: z.string().datetime().optional(),
  orderBy: z.string().optional(),
  orderDirection: z.enum(['asc', 'desc']).optional(),
  state: oneOrMany(z.nativeEnum(FlowRunState)).optional(),
  groupId: z.uuid().optional(),
  projectBlockOutputColumns: z.boolean().optional(),
  blockOutputColumns: z.array(z.string()).optional(),
});

const _flowRunExploreGetColumnsRequestQuerySchema = z.object({
  flowId: z.uuid(),
});

const flowRunExploreColumnTypeSchema = z.enum(['FlowRun', 'BlockOutput']);

const flowRunExploreColumnSchema = z.object({
  key: z.string(),
  displayName: z.string(),
  type: flowRunExploreColumnTypeSchema,
});

const flowRunExploreRequestColumnSchema = flowRunExploreColumnSchema.partial({
  displayName: true,
});

const _flowRunExploreExportRequestSchema = _flowRunExploreFindAllRequestQuerySchema
  .partial({
    limit: true,
    offset: true,
  })
  .extend({
    columns: z.array(flowRunExploreRequestColumnSchema),
  });

const _flowRunExploreGetColumnsResponseSchema = z.object({
  columns: z.array(flowRunExploreColumnSchema),
});

const _flowRunExploreExportResponseSchema = z.object({
  spreadsheetId: z.string().nullish(),
  spreadsheetUrl: z.string().nullish(),
});

type FlowRunExploreColumnType = z.infer<typeof flowRunExploreColumnTypeSchema>;

export type FlowRunExploreColumn = z.infer<typeof flowRunExploreColumnSchema>;

export type FlowRunExploreRequestColumn = z.infer<typeof flowRunExploreRequestColumnSchema>;

export type FlowRunExploreFindAllOptions = z.infer<typeof _flowRunExploreFindAllRequestQuerySchema>;

export type FlowRunExploreGetColumnsOptions = z.infer<
  typeof _flowRunExploreGetColumnsRequestQuerySchema
>;

export type FlowRunExploreExportOptions = z.infer<typeof _flowRunExploreExportRequestSchema>;

export enum FlowRunExploreCellOutputStatus {
  Inline = 'Inline',
  Omitted = 'Omitted',
}

export interface FlowRunExploreCellValue {
  value: unknown;
  sizeBytes?: number;
  outputStatus?: FlowRunExploreCellOutputStatus;
}

export interface FlowRunExploreFindAllResponse {
  data: {
    id: string;
    [key: `${FlowRunExploreColumnType}:${string}`]: FlowRunExploreCellValue | undefined;
  }[];
  columns: FlowRunExploreColumn[];
}

export type FlowRunExploreBlockShell = Omit<FlowRunBlockWithData, 'data' | 'metadata'>;

export type FlowRunExploreRunShell = Omit<FlowRunWithData, 'data'> & {
  data: { blocks: Record<string, FlowRunExploreBlockShell> };
};

export type FlowRunExploreGetColumnsResponse = z.infer<
  typeof _flowRunExploreGetColumnsResponseSchema
>;

export type FlowRunExploreExportResponse = z.infer<typeof _flowRunExploreExportResponseSchema>;
export interface FlowRunExploreDownloadResponse {
  downloadUrl: string;
}
